import { closeModal, openModal } from '@mantine/modals'
import { Anchor, Button, Divider, Group, Stack, Switch, Text, TextInput, useMantineTheme } from '@mantine/core'
import React, { useMemo, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { isNotEmpty, useForm } from '@mantine/form'
import getDefaultLocaleWords from 'components/business/CampaignBuilder/StyleSetting/Locale/utils/getDefaultLocaleWords'
import { Application, CampaignLocaleSettingInput, CampaignType, Direction, Locale, WordingsInput } from 'types/domain'
import { TextInputProps } from '@mantine/core/lib/TextInput/TextInput'
import { getTextColor } from 'config/themeOverride/getColor'
import HelpDocTooltip from 'components/ui/HelpDocTooltip'
import { hideNextButton, hideOtherChoicePlaceholder, hideScreenshot } from './utils/wordingInputAvailable'
import { isWebDistribution } from 'utils/applicationUtils'
import { calculateInitialValues } from './utils/calculateInitialValues'

const wordingModalId = 'wordingModalId'
type Props = {
  localeSetting: CampaignLocaleSettingInput
  direction: Direction
  onConfirm: (wordings: WordingsInput, direction: Direction) => void
  onDirectionChange: (direction: Direction) => void
  application: Application
  campaignType: CampaignType
}

const FormTextInput = (props: TextInputProps) => {
  const theme = useMantineTheme()
  const { label, ...others } = props
  return (
    <Group noWrap>
      <Text color={getTextColor('gray.9', theme)} sx={{ whiteSpace: 'nowrap' }} w={120} weight={600}>
        {label}
      </Text>
      <TextInput {...others} size="sm" className="w-full" />
    </Group>
  )
}

const ModalContent = ({
  localeSetting,
  direction: initialDirection,
  application,
  campaignType,
  onConfirm,
  onDirectionChange,
}: Props) => {
  const { t } = useTranslation('componentCampaignBuilder')
  const [direction, setDirection] = useState(initialDirection)
  const defaultLocaleWords = useMemo(() => getDefaultLocaleWords(localeSetting?.locale), [localeSetting?.locale])
  const initialValues = useMemo(() => {
    return calculateInitialValues(defaultLocaleWords, localeSetting)
  }, [defaultLocaleWords, localeSetting])

  const form = useForm({
    initialValues,
    validateInputOnBlur: true,
    validate: {
      nextButton: isNotEmpty(t('common:message.valueRequired')),
      submitButton: isNotEmpty(t('common:message.valueRequired')),
      otherChoicePlaceholder: isNotEmpty(t('common:message.valueRequired')),
      screenshotDescription: {
        screenshot: isNotEmpty(t('common:message.valueRequired')),
        captured: isNotEmpty(t('common:message.valueRequired')),
      },
    },
  })

  const handleDirectionChange = (checked: boolean) => {
    const dir = checked ? Direction.Rtl : Direction.Ltr
    setDirection(dir)
    onDirectionChange(dir)
  }

  const handleSubmit = async () => {
    onConfirm(form.getTransformedValues(), direction)
    closeModal(wordingModalId)
  }

  const resetToDefault = () => {
    form.setValues(defaultLocaleWords)
  }

  const theme = useMantineTheme()

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack spacing="xl">
        <Text size="sm" color="dimmed">
          {t('styles.locale.form.desc')}
        </Text>
        <Stack spacing="sm">
          {localeSetting?.locale === Locale.Other && isWebDistribution(application) && (
            <>
              <Group noWrap position="apart">
                <Group noWrap spacing={0}>
                  <Text color={getTextColor('gray.9', theme)} weight={600} sx={{ whiteSpace: 'nowrap' }}>
                    {t('styles.locale.form.directionRtl')}
                  </Text>
                  <HelpDocTooltip withinPortal zIndex={1001}>
                    {t('styles.locale.form.directionTip')}
                  </HelpDocTooltip>
                </Group>
                <Switch
                  checked={direction === Direction.Rtl}
                  onChange={(e) => handleDirectionChange(e.currentTarget.checked)}
                />
              </Group>
              <Divider />
            </>
          )}
          {!hideNextButton(campaignType) && (
            <FormTextInput label={t('styles.locale.form.nextButton')} {...form.getInputProps('nextButton')} />
          )}
          <FormTextInput label={t('styles.locale.form.submitButton')} {...form.getInputProps('submitButton')} />
          {!hideOtherChoicePlaceholder(campaignType) && (
            <FormTextInput
              label={t('styles.locale.form.otherChoicePlaceholder')}
              {...form.getInputProps('otherChoicePlaceholder')}
            />
          )}
          {!hideScreenshot(application) && (
            <>
              <FormTextInput
                label={t('styles.locale.form.screenshotDescription.screenshot')}
                {...form.getInputProps('screenshotDescription.screenshot')}
              />
              <FormTextInput
                label={t('styles.locale.form.screenshotDescription.captured')}
                {...form.getInputProps('screenshotDescription.captured')}
              />
            </>
          )}
        </Stack>
        <Group position="apart">
          <Group spacing="xs">
            <Anchor onClick={() => resetToDefault()}>恢复默认</Anchor>|
            <Anchor onClick={() => form.reset()}>重置修改</Anchor>
          </Group>
          <Group position="right">
            <Button variant="default" onClick={() => closeModal(wordingModalId)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit">{t('common:actions.confirm')}</Button>
          </Group>
        </Group>
      </Stack>
    </form>
  )
}

export default function useWordingsModal({
  localeSetting,
  direction,
  application,
  campaignType,
  onConfirm,
  onDirectionChange,
}: Props) {
  const { t } = useTranslation('componentCampaignBuilder')

  return () => {
    openModal({
      modalId: wordingModalId,
      closeOnClickOutside: false,
      closeOnEscape: false,
      title: (
        <Text size="md" weight={600}>
          {t('styles.locale.form.title')}
        </Text>
      ),
      children: (
        <ModalContent
          localeSetting={localeSetting}
          direction={direction}
          application={application}
          campaignType={campaignType}
          onConfirm={onConfirm}
          onDirectionChange={onDirectionChange}
        />
      ),
      zIndex: 1000,
      centered: true,
      styles: {
        header: {
          marginBottom: 0,
        },
      },
    })
  }
}
